/**
 * Which facility and anesthesia partner we pay for a procedure at each market
 * (BAR-3042). An `OrganizationAffiliation` links the partner to the market
 * Location in a role, over a period, so a market's history survives a change
 * of facility. Not a field on the Location: `market-locations.json` is PUT on
 * every data deploy and would wipe it.
 */

import { createReference, getReferenceString, MedplumClient } from '@medplum/core';
import { Location, Organization, OrganizationAffiliation, Reference } from '@medplum/fhirtypes';
import { OrganizationType } from './constants';

export const PARTNER_ROLE_SYSTEM = 'http://bariendo.com/fhir/CodeSystem/partner-role';

export type PartnerRole = 'facility' | 'anesthesia';

export const PARTNER_ROLES: readonly PartnerRole[] = ['facility', 'anesthesia'];

/** A facility can also be paid for anesthesia; an anesthesia partner is never a facility. */
export function partnerRoles(organization: Organization): PartnerRole[] {
  const type = organization.type?.[0]?.coding?.[0]?.code;
  if (type === OrganizationType.FACILITY) return ['facility', 'anesthesia'];
  if (type === OrganizationType.ANESTHESIA) return ['anesthesia'];
  return [];
}

export function affiliationRole(affiliation: OrganizationAffiliation): PartnerRole | undefined {
  const code = affiliation.code?.flatMap((c) => c.coding ?? []).find((c) => c.system === PARTNER_ROLE_SYSTEM)?.code;
  return PARTNER_ROLES.find((role) => role === code);
}

/** Active, and its period covers `on` (`YYYY-MM-DD`); both ends inclusive. */
export function affiliationInForce(affiliation: OrganizationAffiliation, on: string): boolean {
  if (affiliation.active === false) return false;
  const start = affiliation.period?.start?.slice(0, 10);
  const end = affiliation.period?.end?.slice(0, 10);
  return (!start || start <= on) && (!end || end >= on);
}

export function buildAffiliation(input: {
  partner: Organization;
  market: Location;
  role: PartnerRole;
  /** `YYYY-MM-DD`. */
  start: string;
  end?: string;
}): OrganizationAffiliation {
  return {
    resourceType: 'OrganizationAffiliation',
    active: true,
    organization: createReference(input.partner),
    location: [createReference(input.market)],
    code: [{ coding: [{ system: PARTNER_ROLE_SYSTEM, code: input.role }] }],
    period: { start: input.start, ...(input.end ? { end: input.end } : {}) },
  };
}

/**
 * What saving a role's markets does: open an affiliation for each newly picked
 * market, close the in-force one of each dropped market. Closed on `today`, so
 * history is kept and the day's procedures still resolve to it.
 */
export function planAffiliationChanges(input: {
  affiliations: OrganizationAffiliation[];
  role: PartnerRole;
  /** `Location/<id>` references. */
  selected: string[];
  today: string;
}): { open: string[]; close: OrganizationAffiliation[] } {
  const current = input.affiliations.filter(
    (affiliation) => affiliationRole(affiliation) === input.role && affiliationInForce(affiliation, input.today)
  );
  const marketOf = (affiliation: OrganizationAffiliation): string | undefined => affiliation.location?.[0]?.reference;
  const currentMarkets = new Set(current.map(marketOf));
  return {
    open: input.selected.filter((market) => !currentMarkets.has(market)),
    close: current
      .filter((affiliation) => !input.selected.includes(marketOf(affiliation) ?? ''))
      .map((affiliation) => ({ ...affiliation, period: { ...affiliation.period, end: input.today } })),
  };
}

export type MarketPayees = Partial<Record<PartnerRole, Organization>>;

/** The facility and anesthesia partner in force at a market on `on` (`YYYY-MM-DD`). */
export async function marketPayees(
  medplum: MedplumClient,
  market: Location | Reference<Location>,
  on: string
): Promise<MarketPayees> {
  const affiliations = await medplum.searchResources('OrganizationAffiliation', {
    location: getReferenceString(market),
    _count: '100',
  });
  const payees: MarketPayees = {};
  for (const affiliation of affiliations) {
    const role = affiliationRole(affiliation);
    if (role && affiliation.organization && affiliationInForce(affiliation, on)) {
      payees[role] = await medplum.readReference(affiliation.organization);
    }
  }
  return payees;
}
